import React, { FC, PropsWithChildren } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link, LinkProps } from "react-router-dom";
import { dashboardClickableTileStyles } from "./dashboardClickableTileStyles";

type TileVariant = "default" | "album" | "schedulePatient" | "photoPicker";

const tileVariantStyles: Record<TileVariant, ReturnType<typeof css>> = {
  default: css`
    border-radius: 12px;
    border-width: 1px;
    padding: 12px 20px;
  `,
  album: css`
    border-radius: 4px;
    border-width: 1px;
    padding: 10px 20px 16px;
  `,
  schedulePatient: css`
    border-radius: 12px;
    border-width: 1px;
    padding: 16px 24px;
  `,
  photoPicker: css`
    border-radius: 12px;
    border-width: 1px;
    padding: 0;
  `,
};

const StyledLink = styled(Link)<{ tileVariant: TileVariant }>`
  ${dashboardClickableTileStyles}
  ${({ tileVariant }) => tileVariantStyles[tileVariant]}
`;

const StyledButton = styled.button<{ tileVariant: TileVariant }>`
  ${dashboardClickableTileStyles}
  ${({ tileVariant }) => tileVariantStyles[tileVariant]}
  appearance: none;
  background: transparent;
  font: inherit;
  text-align: inherit;
`;

export interface DashboardClickableTileProps extends PropsWithChildren {
  to: LinkProps["to"];
  tileVariant?: TileVariant;
  className?: string;
}

export const DashboardClickableTile: FC<DashboardClickableTileProps> = ({
  to,
  tileVariant = "default",
  className,
  children,
}) => {
  return (
    <StyledLink
      to={to}
      tileVariant={tileVariant}
      className={["dashboard-clickable-tile", className].filter(Boolean).join(" ")}
    >
      {children}
    </StyledLink>
  );
};

export interface DashboardClickableTileButtonProps extends PropsWithChildren {
  onClick: () => void;
  tileVariant?: TileVariant;
  className?: string;
  type?: "button" | "submit" | "reset";
}

export const DashboardClickableTileButton: FC<DashboardClickableTileButtonProps> = ({
  onClick,
  tileVariant = "default",
  className,
  type = "button",
  children,
}) => {
  return (
    <StyledButton
      type={type}
      onClick={onClick}
      tileVariant={tileVariant}
      className={["dashboard-clickable-tile", className].filter(Boolean).join(" ")}
    >
      {children}
    </StyledButton>
  );
};
